import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Log, ProjectCoreMode } from '@zerops/models/project';
import { Process } from '@zerops/models/process';
import { VpnSetting, VpnSettings } from '@zerops/zerops/core/project-base';
import { LogInfoRes, LogRes } from '@zerops/zerops/feature/trlog';
import { ProjectEnvPayload, RemoteLoggingDataPayload, VpnPublicKeyPayload } from './project-base.model';
import { L7ConfigResponse, L7ConfigPayload } from '@zerops/models/project';

@Injectable({ providedIn: 'root' })
export class ProjectBaseApi {
  constructor(private _http: HttpClient) { }

  loadTags$(clientId: string) {
    return this._http.get<{ items: string[]; }>(`/api/client/${clientId}/project/tag/list`);
  }

  logUrl$(id: string, instanceId?: string) {
    if (instanceId) {
      return this._http.get<Log>(`/api/project/${id}/log?instanceId=${instanceId}`);
    }
    return this._http.get<Log>(`/api/project/${id}/log`);
  }

  logData$(url: string) {
    return this._http.get<LogRes>(url);
  }

  logInfo$(url: string) {
    return this._http.get<LogInfoRes>(url);
  }

  requestIpv4$(projectId: string) {
    return this._http.put<Process>(
      `/api/project/${projectId}/request-ipv4`,
      {}
    );
  }

  releaseIpv4$(projectId: string) {
    return this._http.put<Process>(
      `/api/project/${projectId}/release-ipv4`,
      {}
    );
  }

  startProject$(id: string) {
    return this._http.put<Process>(
      `/api/project/${id}/start`,
      {}
    );
  }

  updateRemoteLogging$(id: string, data: RemoteLoggingDataPayload) {
    return this._http.put<Process>(
      `/api/project/${id}/remote-logging`,
      data
    );
  }

  addVpnPublicKey$(id: string, data: VpnPublicKeyPayload) {
    return this._http.post<VpnSetting>(
      `/api/project/${id}/vpn`,
      data
    );
  }

  deleteVpnPublicKey$(id: string, data: VpnPublicKeyPayload) {
    const options = {
      body: {
        publicKey: data.publicKey
      }
    };
    return this._http.delete<{success: true}>(
      `/api/project/${id}/vpn`,
      options
    );
  }

  listVpnPublicKeys$(id: string) {
    return this._http.get<VpnSettings>(
      `/api/project/${id}/vpn/list`
    );
  }

  projectExport$(id: string, reveal?: boolean) {
    return this._http.get<{ yaml: string; }>(
      `/api/project/${id}/export${reveal ? '?reveal=true' : ''}`
    );
  }

  // TODO -- new endpoint return 403, the old one not
  projectImport$(
    data: {
      yaml: string;
      recipeSource?: string;
      recipeSourceUrl?: string;
    },
    clientId: string
  ) {
    return this._http.post<any>(
      `/api/client/${clientId}/project/import`,
      {
        ...data,
      }
    );
  }

  addProjectCustomEnv$(projectId: string, data: ProjectEnvPayload) {
    return this._http.post<Process>(
      `/api/project/${projectId}/env`,
      data
    );
  }

  /**
   * Update all project custom environment variables through a bulk operation.
   * It is used a stringified list of all variables as a content.
   */
  updateProjectCustomEnvs$(projectId: string, content: string, sensitive?: boolean) {
    return this._http.put<Process>(
      `/api/project/${projectId}/env/file`,
      { envFile: content, sensitive }
    );
  }

  /**
   * Bulk-reveal endpoint: returns every user-defined project env of the requested
   * category (sensitive or not) pre-serialized as a round-trippable `.env` string.
   * Sudo is required only when `sensitive=true`; the same value must be sent back to
   * the `updateProjectCustomEnvs$` PUT so the correct category is rewritten.
   */
  getProjectEnvFile$(projectId: string, sensitive?: boolean) {
    return this._http.get<{ envFile: string; }>(
      `/api/project/${projectId}/project-env/env-file`,
      { params: { sensitive: `${!!sensitive}` } }
    );
  }

  updateProjectEnv$(id: string, data: ProjectEnvPayload) {
    return this._http.put<Process>(
      `/api/project-env/${id}`,
      data
    );
  }

  deleteProjectCustomEnv$(id: string) {
    return this._http.delete<Process>(
      `/api/project-env/${id}`
    );
  }

  revealProjectEnv$(id: string) {
    return this._http.get<{ content: string; }>(
      `/api/project-env/${id}/reveal`
    );
  }

  updateProjectMode$(projectId: string, mode: ProjectCoreMode) {
    return this._http.put<Process>(`/api/project/${projectId}/mode-upgrade`, { mode });
  }

  getL7HttpBalancerConfig$(projectId: string) {
    return this._http.get<L7ConfigResponse>(`/api/project/${projectId}/l7httpbalancer-config`);
  }

  updateL7HttpBalancerConfig$(projectId: string, data: L7ConfigPayload) {
    return this._http.put<Process>(`/api/project/${projectId}/l7httpbalancer-config`, data);
  }

  updateIPv4Shared(projectId: string, enabled: boolean) {
    return this._http.put<Process>(`/api/project/${projectId}/public-ipv4-shared`, { enabled });
  }

}
